import { Box, Link } from 'braid-design-system';
import { Fragment, type ReactNode } from 'react';

import { LegacyAdviceAndTipsIcon } from '../LegacyIcons/LegacyAdviceAndTipsIcon';
import { LegacyFindBrokersIcon } from '../LegacyIcons/LegacyFindBrokersIcon';
import { LegacyFranchiseIcon } from '../LegacyIcons/LegacyFranchiseIcon';
import { LegacyHeartIcon } from '../LegacyIcons/LegacyHeartIcon';
import { LegacyPreferencesIcon } from '../LegacyIcons/LegacyPreferencesIcon';
import { LegacyProfileIcon } from '../LegacyIcons/LegacyProfileIcon';
import { LegacySearchIcon } from '../LegacyIcons/LegacySearchIcon';
import { LegacySellABusinessIcon } from '../LegacyIcons/LegacySellABusinessIcon';
import { LegacyStarIcon } from '../LegacyIcons/LegacyStarIcon';
import {
  createNavLinks,
  dropdownGenericCloseDataAttr,
  navLinkIds,
} from '../utils/generalUtils';

import { NavHeading } from './DropdownHeading';

import * as styles from './DropdownButtons.css';

interface DropdownButtonInstanceProps {
  baseUrl: string;
}

export const MyPreferencesButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.buyerPreferences}
    href={createNavLinks(baseUrl).buyerPreferences}
    name="My preferences"
    icon={<LegacyPreferencesIcon className={styles.icon} />}
  />
);

export const FranchiseButton = ({ baseUrl }: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.franchise}
    href={createNavLinks(baseUrl).franchise}
    name="Franchise"
    icon={<LegacyFranchiseIcon className={styles.icon} />}
  />
);

export const FindBrokersButton = ({ baseUrl }: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.findBrokers}
    href={createNavLinks(baseUrl).findBrokers}
    name="Find brokers"
    icon={<LegacyFindBrokersIcon className={styles.icon} />}
  />
);

export const AdviceAndTipsButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.adviceAndTips}
    href={createNavLinks(baseUrl).adviceAndTips}
    name="Advice & Tips"
    icon={<LegacyAdviceAndTipsIcon className={styles.icon} />}
  />
);

export const MyProfileButton = ({ baseUrl }: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.myProfile}
    href={createNavLinks(baseUrl).myProfile}
    name="My profile"
    icon={<LegacyProfileIcon tone="purple" className={styles.icon} />}
  />
);

export const SavedBusinessesButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.savedBusinesses}
    href={createNavLinks(baseUrl).savedBusinesses}
    name="Saved businesses"
    icon={<LegacyHeartIcon className={styles.icon} />}
  />
);

export const SavedSearchesButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.savedSearches}
    href={createNavLinks(baseUrl).savedSearches}
    name="Saved searches"
    icon={<LegacyStarIcon className={styles.icon} />}
  />
);

export const BusinessSearchButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.businessSearch}
    href={createNavLinks(baseUrl).home}
    name="Business search"
    icon={<LegacySearchIcon className={styles.icon} />}
  />
);

// This is the same as above <BusinessSearchButton />, do we need both?
export const BuyABusinessButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.buyABusiness}
    href={createNavLinks(baseUrl).home}
    name="Buy a business"
    icon={<LegacySearchIcon className={styles.icon} />}
  />
);

export const SignOutButton = ({ baseUrl }: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.signOut}
    href={createNavLinks(baseUrl).signOut}
    name="Sign out"
    icon={null}
  />
);

export const AdvertisersButton = ({ baseUrl }: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.advertisers}
    href={createNavLinks(baseUrl).advertisers}
    name="Advertisers"
    icon={null}
  />
);

// This is the same as above <AdvertisersButton />, do we need both?
export const AdvertiseWithUsButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.advertiseWithUs}
    href={createNavLinks(baseUrl).advertisers}
    name="Advertise with us"
    icon={null}
  />
);

export const SellABusinessButton = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <DropdownButton
    id={navLinkIds.sellABusiness}
    href={createNavLinks(baseUrl).sellABusiness}
    name="Sell a business"
    icon={<LegacySellABusinessIcon className={styles.icon} />}
  />
);

interface ManageMyAdsButtonProps {
  advertiserCentreUrl: string;
}

export const ManageMyAdsButton = ({
  advertiserCentreUrl,
}: ManageMyAdsButtonProps) => (
  <DropdownButton
    id={navLinkIds.advertiserCentre}
    href={advertiserCentreUrl}
    name="Manage my ads"
    icon={null}
  />
);

export const BuyingABusinessSection = ({
  baseUrl,
}: DropdownButtonInstanceProps) => (
  <Fragment>
    <NavHeading>Buying a business</NavHeading>
    <MyProfileButton baseUrl={baseUrl} />
    <MyPreferencesButton baseUrl={baseUrl} />
    <SavedBusinessesButton baseUrl={baseUrl} />
    <SavedSearchesButton baseUrl={baseUrl} />
    <BusinessSearchButton baseUrl={baseUrl} />
    <FranchiseButton baseUrl={baseUrl} />
    <FindBrokersButton baseUrl={baseUrl} />
    <AdviceAndTipsButton baseUrl={baseUrl} />
  </Fragment>
);

// ============================================================================
// DropdownButton
// ============================================================================

interface DropdownButtonProps {
  id: string;
  href: string;
  name: string;
  icon: ReactNode;
}
export const DropdownButton = ({
  id,
  href,
  name,
  icon,
}: DropdownButtonProps) => (
  <Link
    id={id}
    href={href}
    className={styles.dropdownButton}
    data={{
      [dropdownGenericCloseDataAttr]: '',
      htag: id,
    }}
  >
    <Box className={styles.dropdownButtonInner}>
      <span className={styles.dropdownButtonText}>{name}</span>
      <Box className={styles.dropdownButtonIcon}>{icon}</Box>
    </Box>
  </Link>
);
